import React, { Component } from 'react';
import '../static/css/AppStoreButtons.css';
import appStoreBadge from '../static/images/images/download-on-the-app-store-apple-logo-svgrepo-com.svg';
import googlePlayBadge from '../static/images/images/google-play-badge-logo-svgrepo-com.svg';
import { getSource, recordAction, onSourceChange } from '../tracking';

// Single source of truth for the live store listings. Both platforms are live.
const APP_STORE_URL = 'https://apps.apple.com/us/app/animates/id6744946574';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.animates.app';

// 🔑 THIS IS THE QUALITY METRIC. A click says who showed up; a store tap says who
//    cared. A platform sending 800 clicks at a 2% tap rate is worse than one
//    sending 90 at 30%, and nothing else in the dataset can tell the two apart.
//
// The campaign parameters below are the OTHER half, and they are free: neither
// store lets us see an install, but both will report installs bucketed by a
// campaign value we append to the link.
//
// 🔴 App Store `pt` (the App Store Connect provider token) is NOT set, because the
//    owner has not supplied one and inventing a value would silently break the
//    link. ⚠️ BE HONEST ABOUT WHAT THAT COSTS: Apple documents `pt` and `ct` as a
//    PAIR, and it is not verified that `ct` reports anything on its own — the iOS
//    half of the campaign work may be recording nothing until the owner reads his
//    provider token out of App Store Connect. The link itself works either way
//    (verified: the listing opens with ?ct=&mt=8 attached), and adding `pt` later
//    is a one-constant change on the line below. Named as a blocking follow-up.
const APP_STORE_PROVIDER_TOKEN = null;

// Each surface keeps its own badge sizing/layout via its existing CSS classes; variant maps to them.
const ROW_CLASS = {
    signup: 'signup-success-store-row',
    home: 'home-app-promo-row',
    welcome: 'welcomecs-app-stores-row',
    eventdetail: 'event-detail-store-row',
};
const LINK_CLASS = {
    signup: 'signup-success-store-link',
    home: 'home-app-promo-link',
    welcome: 'welcomecs-app-store-link',
    eventdetail: 'event-detail-store-link',
};

class AppStoreButtons extends Component {
    constructor(props) {
        super(props);
        this.state = { source: getSource() };
        this.onAppStoreClick = this.onAppStoreClick.bind(this);
        this.onPlayStoreClick = this.onPlayStoreClick.bind(this);
        this.onSourceChange = this.onSourceChange.bind(this);
    }

    // 🔑 THE ORDERING PROBLEM THIS SOLVES. On the very first landing at /?s=zh8b the
    //    beacon's response — the only thing that knows the PLATFORM and PLACEMENT —
    //    arrives a few hundred milliseconds after this component has already
    //    rendered. Read once at render, the most important page view in the funnel
    //    (the one that just came from the ad) would build plain store links and
    //    never correct them. A visitor arriving with a source already stored, or via
    //    /<code>, is unaffected either way.
    componentDidMount() {
        this.unsubscribeSource = onSourceChange(this.onSourceChange);
    }

    componentWillUnmount() {
        if (this.unsubscribeSource) this.unsubscribeSource();
    }

    onSourceChange() {
        this.setState({ source: getSource() });
    }

    // Never preventDefault, never await: the browser follows the link immediately and
    // the beacon rides along on fetch(keepalive) rather than being cancelled by the
    // navigation. recordAction swallows everything, so neither of these can throw.
    onAppStoreClick() {
        recordAction('store_click_ios', 'apps.apple.com');
    }

    onPlayStoreClick() {
        recordAction('store_click_android', 'play.google.com');
    }

    // App Store Connect → App Analytics → Acquisition → Campaigns buckets by `ct`.
    appStoreUrl(source) {
        if (!source || !source.code) return APP_STORE_URL;
        let url = APP_STORE_URL + '?ct=' + encodeURIComponent(source.code) + '&mt=8';
        if (APP_STORE_PROVIDER_TOKEN) url += '&pt=' + encodeURIComponent(APP_STORE_PROVIDER_TOKEN);
        return url;
    }

    // Play Console → Acquisition reports → traffic sources buckets by utm_source.
    // The whole utm string is ONE parameter value, so it is encoded as a unit.
    //
    // 🔑 utm_source carries the readable PLATFORM, not the code — that report is
    //    read by a human and "zh8b" tells him nothing. The code still travels, in
    //    utm_content. When the platform is unknown (the /r/<code> hard redirect
    //    cannot carry it) the code is the honest fallback.
    playStoreUrl(source) {
        if (!source || !source.code) return PLAY_STORE_URL;
        // Each sub-value is encoded before the whole string is encoded again: the
        // platform and placement are free text the owner types into the dashboard,
        // and one '&' in "Anime & Manga Weekly" would otherwise split the referrer
        // into fields Play would then report under the wrong names.
        const part = (name, value) => name + '=' + encodeURIComponent(value);
        const referrer = [
            part('utm_source', source.platform || source.code),
            part('utm_medium', source.placement || 'link'),
            // 🔑 utm_campaign CARRIES THE CODE, and that is a deliberate addition to
            //    the field list in the brief. Play Console's Acquisition report
            //    breaks traffic out by utm_source and utm_campaign only — utm_medium
            //    and utm_content ride along to the Install Referrer API but are not
            //    reported — so without this the code, the one value that identifies
            //    WHICH post or placement drove the install, would never be visible
            //    in the console. utm_content keeps it too, for the app-side API later.
            part('utm_campaign', source.code),
            part('utm_content', source.code),
        ].join('&');
        return PLAY_STORE_URL + '&referrer=' + encodeURIComponent(referrer);
    }

    render() {
        const variant = this.props.variant || 'home';
        const rowClass = ROW_CLASS[variant] || ROW_CLASS.home;
        const linkClass = LINK_CLASS[variant] || LINK_CLASS.home;
        // An untagged visitor gets the plain, working store links — no empty
        // parameters, nothing to break.
        const source = this.state.source;
        return (
            <div className={rowClass}>
                <a href={this.appStoreUrl(source)} target="_blank" rel="noopener noreferrer" className={linkClass} onClick={this.onAppStoreClick}>
                    <img src={appStoreBadge} alt="Download on the App Store" />
                </a>
                <a href={this.playStoreUrl(source)} target="_blank" rel="noopener noreferrer" className={linkClass} onClick={this.onPlayStoreClick}>
                    <img src={googlePlayBadge} alt="Get it on Google Play" />
                </a>
            </div>
        );
    }
}

export default AppStoreButtons;
